import { computed, reactive } from 'vue';
import { defineStore } from 'pinia';
import { assignState } from '@/store/index.js';
import useAppSettingStore from '@/store/modules/appSettings.js';
import useLocaleStore from '@/store/modules/locale.js';
import useLevelStore from '@/store/modules/level.js';
import useBonusPhaseStore from '@/store/modules/bonusPhase.js';
import useNotificationStore from '@/store/modules/notification.js';
import { useLocalStorageHook } from '@/hooks/usePersistentStorage.js';
import { jwtTokenStorageKey, levelUpStorageKey } from '@/constants/persistentStorage.js';
import { achievementCategoryEnum } from '@/constants/reward.js';
import {
  getLevelNumberImage,
  getLevelNameTranslationKey,
  calculateLevelCompletedPercentage,
} from '@/helpers/reward.js';
import {
  apiAuthenticateUser,
  apiRetrieveAuthUserDetails,
  apiRetrieveAuthUserLoginDetails,
  apiRetrieveAuthUserSummary,
  apiUpdateAuthUserLanguage,
  apiRetrieveUserAchievements,
  apiRetrieveUserStatistics,
  apiChangeUserProfileVisibility,
  apiChangeUsername,
  apiChangeAvatar,
  apiChangeEmail,
} from '@/services/user.js';

const getUrlParamsDefaultState = () => ({
  checkId: '',
  email: '',
});

const getAuthUserDefaultState = () => ({
  availableGames: null,
  avatar: null,
  avatarGender: null,
  displayAvatar: null,
  gamesPlayed: null,
  email: null,
  hasEmailVerified: null,
  hasPrivateProfile: null,
  id: null,
  isUsernameUpdated: null,
  jokersCheat: null,
  jokersFifty: null,
  language: null,
  level: null,
  msisdn: null,
  operatorName: null,
  remainingPointsToLevel: null,
  subscribed: null,
  totalPointsEarned: null,
  username: null,
  webUser: null,
});

const getUserStatisticsDefaultState = () => ({
  questionsPlayed: 0,
  correctQuestions: 0,
  perfectGames: 0,
});

const getAchievementsConfigDefaultState = () => ({ totalUnlocked: 0, list: [] });

const useAuthStore = defineStore('auth', () => {
  const localeStore = useLocaleStore();
  const levelStore = useLevelStore();

  const [jwtToken, setJwtToken] = useLocalStorageHook(jwtTokenStorageKey, '');
  const [levelUp, setLevelUp] = useLocalStorageHook(levelUpStorageKey, null);

  const urlParams = reactive(getUrlParamsDefaultState());
  const user = reactive(getAuthUserDefaultState());
  const userStatistics = reactive(getUserStatisticsDefaultState());
  const achievementsConfig = reactive(getAchievementsConfigDefaultState());

  const isUserAuthenticated = computed(() => !!jwtToken.value);
  const userAvatar = computed(() => user.avatar);
  const userUsername = computed(() => user.username);
  const hasUserAvailableGames = computed(() => user.availableGames > 0);
  const userLevelName = computed(() => getLevelNameTranslationKey(user.level));
  const userLevelIcon = computed(() => getLevelNumberImage(user.level));
  const hasUserCompletedAllLevels = computed(() => user.level === levelStore.totalLevels);
  const userNextLevelNumber = computed(() => ((user.level + 1) > levelStore.totalLevels) ? levelStore.totalLevels : (user.level + 1));
  const userNextLevelThreshold = computed(() => levelStore.levelList.find(level => level.__level === userNextLevelNumber.value)?.threshold);
  const userNextLevelGapTotal = computed(() => (userNextLevelThreshold.value > user.gamesPlayed) ? (userNextLevelThreshold.value - user.gamesPlayed) : 0);
  const userNextLevelProgressText = computed(() => {
    const totalCompleted = (userNextLevelThreshold.value > user.gamesPlayed) ? user.gamesPlayed : userNextLevelThreshold.value;

    return `${ totalCompleted ?? 0 } / ${ userNextLevelThreshold.value ?? 0 }`;
  });
  const userNextLevelProgressPercentage = computed(() => calculateLevelCompletedPercentage(hasUserCompletedAllLevels.value, user.gamesPlayed, userNextLevelThreshold.value));

  const setUrlParams = (data) => {
    const { email, ...rest } = data;
    const transformedProps = {};

    if (email) {
      transformedProps.email = decodeURIComponent(email);
    }

    assignState(urlParams, { ...rest, ...transformedProps });
  };

  const resetUrlParams = (keyList) => {
    const defaultState = getUrlParamsDefaultState();

    assignState(
      urlParams,
      keyList.reduce((acc, key) => {
        acc[key] = defaultState[key];

        return acc;
      }, {}),
    );
  };

  const dispatchLogout = () => {
    const bonusPhaseStore = useBonusPhaseStore();
    bonusPhaseStore.resetDailyState();
    bonusPhaseStore.resetBonusGameState();

    setJwtToken('');

    assignState(user, getAuthUserDefaultState());
  };

  const dispatchRetrieveAuthUserLoginDetails = async () => {
    try {
      const { parsedLevelUpData, parsedUserDTOData } = await apiRetrieveAuthUserLoginDetails({
        payload: { language: localeStore.currentLanguage },
      });

      if (parsedLevelUpData.levelUp) {
        setLevelUp({ level: parsedUserDTOData.level, prizeValue: parsedLevelUpData.prizeValue });
      }

      assignState(user, parsedUserDTOData);
    } catch (error) {
      dispatchLogout();

      return Promise.reject(error);
    }
  };

  const dispatchRetrieveAuthUser = async () => {
    try {
      await dispatchRetrieveAuthUserLoginDetails();

      const { parsedData: userDetailsParsedData } = await apiRetrieveAuthUserDetails();

      assignState(user, userDetailsParsedData);
    } catch (error) {
      dispatchLogout();

      return Promise.reject(error);
    }
  };

  const dispatchLogin = async ({ payload }) => {
    try {
      const { parsedData } = await apiAuthenticateUser({ payload });

      setJwtToken(parsedData);

      await apiRetrieveAuthUserSummary();

      await dispatchRetrieveAuthUser();

      if (user.language !== localeStore.currentLanguage) {
        await apiUpdateAuthUserLanguage({ language: localeStore.currentLanguage });
      }

      await Promise.allSettled(getPostLoginRequestList().map(action => action()));
    } catch (error) {
      dispatchLogout();

      return Promise.reject(error);
    }
  };

  const dispatchAutoLogin = async ({ token }) => {
    try {
      setJwtToken(token);

      await dispatchRetrieveAuthUser();

      if (user.language !== localeStore.currentLanguage) {
        await apiUpdateAuthUserLanguage({ language: localeStore.currentLanguage });
      }

      await Promise.allSettled(getPostLoginRequestList().map(action => action()));
    } catch (error) {
      dispatchLogout();

      return Promise.reject(error);
    }
  };

  const dispatchRetrieveUserAchievements = async () => {
    try {
      const appSettingsStore = useAppSettingStore();

      const { parsedData } = await apiRetrieveUserAchievements();

      const achievementList = !appSettingsStore.appProperties.inviteConfig.inviteEnabled
        ? parsedData.achievementList.filter(({ __category }) => __category !== achievementCategoryEnum.SUCCESSFUL_FRIEND_INVITATIONS)
        : parsedData.achievementList;

      assignState(achievementsConfig, { list: achievementList, totalUnlocked: parsedData.totalUnlockedAchievements });
    } catch {
      assignState(achievementsConfig, getAchievementsConfigDefaultState());
    }
  };

  const dispatchRetrieveUserStatistics = async () => {
    try {
      const { parsedData } = await apiRetrieveUserStatistics();

      assignState(userStatistics, parsedData);
    } catch {
      assignState(userStatistics, getUserStatisticsDefaultState());
    }
  };

  const dispatchChangeUserProfileVisibility = async ({ visibility }) => {
    try {
      assignState(user, { hasPrivateProfile: visibility });

      await apiChangeUserProfileVisibility({ queryParams: { visibility } });
    } catch {
      assignState(user, { hasPrivateProfile: !visibility });
    }
  };

  const dispatchChangeUsername = async ({ username }) => {
    try {
      const { parsedData } = await apiChangeUsername({ queryParams: { username } });

      assignState(user, { username, isUsernameUpdated: true });

      setJwtToken(parsedData);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const dispatchChangeAvatar = async ({ avatar }) => {
    const previousAvatar = userAvatar.value;

    try {
      assignState(user, { avatar });

      await apiChangeAvatar({ payload: { avatar } });
    } catch {
      assignState(user, { avatar: previousAvatar });
    }
  };

  const dispatchChangeEmail = async ({ email }) => {
    try {
      await apiChangeEmail({ queryParams: { email } });

      assignState(user, { email });
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const getPostLoginRequestList = () => {
    const appSettingsStore = useAppSettingStore();
    const bonusPhaseStore = useBonusPhaseStore();
    const notificationStore = useNotificationStore();

    return [
      dispatchRetrieveUserAchievements,
      appSettingsStore.dispatchRetrieveMultiOperatorProperties,
      levelStore.dispatchRetrieveLevelList,
      bonusPhaseStore.dispatchRetrieveBonusPhases,
      notificationStore.dispatchRetrieveNotifications,
    ];
  };

  return {
    jwtToken,
    levelUp,
    urlParams,
    user,
    userStatistics,
    achievementsConfig,
    isUserAuthenticated,
    userAvatar,
    userUsername,
    userLevelName,
    userLevelIcon,
    hasUserCompletedAllLevels,
    userNextLevelNumber,
    userNextLevelGapTotal,
    userNextLevelProgressText,
    userNextLevelProgressPercentage,
    hasUserAvailableGames,
    setJwtToken,
    setLevelUp,
    setUrlParams,
    resetUrlParams,
    dispatchLogout,
    dispatchRetrieveAuthUserLoginDetails,
    dispatchRetrieveAuthUser,
    dispatchLogin,
    dispatchAutoLogin,
    dispatchRetrieveUserAchievements,
    dispatchRetrieveUserStatistics,
    dispatchChangeUserProfileVisibility,
    dispatchChangeUsername,
    dispatchChangeAvatar,
    dispatchChangeEmail,
    getPostLoginRequestList,
  };
});

export default useAuthStore;
